import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import { Eye, Edit, Search, RefreshCw, Filter, X } from 'lucide-react';
import CompleteReservationModal from '../components/CompleteReservationModal';
import Sidebar from '../components/Sidebar';
import Swal from "sweetalert2";
import { useTranslation } from 'react-i18next';

const Location = ({ defaultStatus = '' }) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [reservations, setReservations] = useState([]);
    const [clients, setClients] = useState([]);
    const [voitures, setVoitures] = useState([]);
    const [clientDetails, setClientDetails] = useState({});
    const [voitureDetails, setVoitureDetails] = useState({});
    const navigate = useNavigate();
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);

    // Filter state
    const [status, setStatus] = useState(defaultStatus);
    const [clientId, setClientId] = useState('');
    const [voitureId, setVoitureId] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    // Fetch filters and data
    useEffect(() => {
        fetchClients();
        fetchVoitures();
        fetchReservations();
    }, []);

    const fetchClients = async () => {
        try {
            const res = await axios.get('https://krili-loto.com/api/clients');
            setClients(res.data);

            // Create a clients map for easy lookup
            const clientsMap = {};
            res.data.forEach(client => {
                clientsMap[client.id] = client;
            });
            setClientDetails(clientsMap);
        } catch (err) {
            setError(t('failed_load_clients'));
        }
    };

    const fetchVoitures = async () => {
        try {
            const res = await axios.get('https://krili-loto.com/api/voitures');
            setVoitures(res.data);

            // Create a voitures map for easy lookup
            const voituresMap = {};
            res.data.forEach(voiture => {
                voituresMap[voiture.id] = voiture;
            });
            setVoitureDetails(voituresMap);
        } catch (err) {
            setError(t('failed_load_vehicles'));
        }
    };

    const fetchReservations = async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                page: currentPage,
                limit: itemsPerPage
            };
            if (status) params.status = status;
            if (clientId) params.client_id = clientId;
            if (voitureId) params.voiture_id = voitureId;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;

            const res = await axios.get('https://krili-loto.com/api/reservations/status/en_cours', { params });

            // Handle server-side pagination response
            if (res.data.data) {
                setReservations(res.data.data);
                setTotalPages(res.data.totalPages);
                setTotalItems(res.data.total);
            } else {
                setReservations(res.data);
            }

            // Fetch additional details for each reservation
            const reservationsToFetch = res.data.data || res.data;
            await fetchReservationDetails(reservationsToFetch);
        } catch (err) {
            setError(t('failed_load_reservations'));
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch when page changes
    useEffect(() => {
        fetchReservations();
    }, [currentPage]);
    const handleDeleteReservation = async (id) => {
        Swal.fire({
            title: t('confirm_delete_title'),
            text: t('confirm_delete_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('yes_delete'),
            cancelButtonText: t('cancel'),
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`https://krili-loto.com/api/reservations/${id}`);
                    fetchReservations();
                    Swal.fire(
                        t('deleted_title'),
                        t('deleted_success_text'),
                        'success'
                    );
                } catch (err) {
                    console.error('Erreur lors de la suppression:', err);
                    setError(t('delete_error_short'));
                    Swal.fire(
                        t('error'),
                        t('delete_error_text'),
                        'error'
                    );
                }
            }
        });
    };

    const fetchReservationDetails = async (reservations) => {
        try {
            // Fetch client details for each unique client ID
            const uniqueClientIds = [...new Set(reservations.map(res => res.client_id))];
            const clientPromises = uniqueClientIds.map(clientId =>
                axios.get(`https://krili-loto.com/api/clients/${clientId}`)
            );

            const clientResponses = await Promise.all(clientPromises);
            const updatedClientDetails = { ...clientDetails };
            clientResponses.forEach(response => {
                updatedClientDetails[response.data.id] = response.data;
            });
            setClientDetails(updatedClientDetails);

            // Fetch vehicle details for each unique vehicle ID
            const uniqueVoitureIds = [...new Set(reservations.map(res => res.voiture_id))];
            const voiturePromises = uniqueVoitureIds.map(voitureId =>
                axios.get(`https://krili-loto.com/api/voitures/${voitureId}`)
            );

            const voitureResponses = await Promise.all(voiturePromises);
            const updatedVoitureDetails = { ...voitureDetails };
            voitureResponses.forEach(response => {
                updatedVoitureDetails[response.data.id] = response.data;
            });
            setVoitureDetails(updatedVoitureDetails);

        } catch (err) {
            console.error('Error fetching reservation details:', err);
        }
    };

    const getClientName = (clientId) => {
        const client = clientDetails[clientId];
        if (client) {
            return `${client.prenom} ${client.nom}`;
        }
        return `${t('client')} #${clientId}`;
    };

    const getVoitureInfo = (voitureId) => {
        const voiture = voitureDetails[voitureId];
        if (voiture) {
            return `${voiture.marque} ${voiture.modele} `;
        }
        return `${t('vehicle')} #${voitureId}`;
    };

    const handleCompleteReservation = (reservation) => {
        setSelectedReservation(reservation);
        setShowCompleteModal(true);
    };

    const submitCompletion = async (payload) => {
        try {
            const res = await axios.put(`https://krili-loto.com/api/reservations/complete/${payload.id}`, payload);

            // Si le backend indique qu'une confirmation est requise
            if (res.data?.needsUserInput) {
                const confirmer = window.confirm(t('confirm_late_closing'));

                if (!confirmer) return; // L'utilisateur a annulé

                // Renvoi avec confirmation
                await axios.put(`https://krili-loto.com/api/reservations/complete/${payload.id}`, {
                    ...payload,
                    allowIncomplete: true, // if reste to pay
                    confirmLate: true,
                    confirm_late: true // Ce champ sera utilisé côté backend pour forcer la clôture
                });
            }

            // Rafraîchit les données après clôture
            fetchReservations();
            setShowCompleteModal(false);
        } catch (err) {
            console.error("Erreur lors de la clôture :", err);
            setError(t('complete_error'));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return moment(dateString).format('DD/MM/YYYY HH:mm');
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-SA' : 'fr-FR', {
            style: 'currency',
            currency: 'DZD'
        }).format(amount).replace('DZD', t('currency')).replace('د.ج.‏', t('currency'));
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'en_attente': 'bg-amber-100 text-amber-800',
            'en_cours': 'bg-blue-100 text-blue-800',
            'done': 'bg-green-100 text-green-800',
            'canceled': 'bg-red-100 text-red-800',
            'en_retard': 'bg-orange-100 text-orange-800'
        };

        const statusLabel = t(status) || status;

        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClasses[status]}`}>
                {statusLabel}
            </span>
        );
    };

    const handleViewDetails = (id) => {
        navigate(`/reservations/${id}`);
    };

    const handleEditReservation = (id) => {
        navigate(`/reservations/${id}/edit`);
    };

    const clearFilters = () => {
        setStatus('');
        setClientId('');
        setVoitureId('');
        setDateFrom('');
        setDateTo('');
        setCurrentPage(1);
    };

    // Pagination logic
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = indexOfFirstItem + reservations.length;

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <Sidebar>
            <div className="p-6 bg-gray-50 min-h-screen" dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="max-w-7xl mx-auto">
                    {showCompleteModal && selectedReservation && (
                        <CompleteReservationModal
                            isOpen={showCompleteModal}
                            onClose={() => setShowCompleteModal(false)}
                            reservation={selectedReservation}
                            onComplete={submitCompletion}
                        />
                    )}

                    {/* Header */}
                    <div className={`flex justify-between items-center mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <h1 className="text-2xl font-bold text-gray-800">{t('reservations_management')}</h1>
                        <div className={`flex ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <Filter className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                {t('filters')}
                            </button>
                            <button
                                onClick={fetchReservations}
                                className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'} ${loading ? 'animate-spin' : ''}`} />
                                {t('refresh')}
                            </button>
                        </div>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div className="bg-white p-4 rounded-lg shadow-md mb-6 border border-gray-200">
                            <div className={`flex justify-between items-center mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <h2 className="text-lg font-medium text-gray-800">{t('filter_reservations')}</h2>
                                <button
                                    onClick={clearFilters}
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                >
                                    <X className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} /> {t('clear_all')}
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('client')}</label>
                                    <select
                                        value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">{t('all_clients')}</option>
                                        {clients.map((client) => (
                                            <option key={client.id} value={client.id}>
                                                {client.prenom} {client.nom}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicle')}</label>
                                    <select
                                        value={voitureId}
                                        onChange={(e) => setVoitureId(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">{t('all_vehicles')}</option>
                                        {voitures.map((voiture) => (
                                            <option key={voiture.id} value={voiture.id}>
                                                {voiture.marque} {voiture.modele}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('departure_date_from')}</label>
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('departure_date_to')}</label>
                                    <input
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={fetchReservations}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    disabled={loading}
                                >
                                    <Search className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                    {loading ? t('searching') : t('search')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Results */}
                    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                        <div className={`px-4 py-5 sm:px-6 border-b border-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {t('results')}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {t('results_description')}
                            </p>
                        </div>

                        {loading ? (
                            <div className="flex justify-center items-center p-8">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                            </div>
                        ) : error ? (
                            <div className="p-4 bg-red-50 border-l-4 border-red-400">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-red-700">
                                            {error}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : reservations.length === 0 ? (
                            <div className="p-8 text-center">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_reservations_found')}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('try_different_filters')}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                    ID
                                                </th>
                                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                    {t('client')}
                                                </th>
                                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                    {t('vehicle')}
                                                </th>
                                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                    {t('dates')}
                                                </th>
                                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                    {t('price')}
                                                </th>
                                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                    {t('status')}
                                                </th>
                                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                    {t('action')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {reservations.map((res) => (
                                                <tr key={res.id} className="hover:bg-gray-50">
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${isRtl ? 'text-right' : 'text-left'}`}>
                                                        #{res.id}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                        <div className="text-sm font-medium text-gray-900">{getClientName(res.client_id)}</div>
                                                        <div className="text-sm text-gray-500">ID: {res.client_id}</div>
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                        <div className="text-sm font-medium text-gray-900">{getVoitureInfo(res.voiture_id)}</div>
                                                        <div className="text-sm text-gray-500">
                                                            {res.lieu_depart} → {res.lieu_retour}
                                                        </div>
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                        <div className="text-sm text-gray-900"><span className="font-medium">{t('departure_point')}</span> {formatDate(res.date_depart)}</div>
                                                        <div className="text-sm text-gray-500"><span className="font-medium">{t('return_point')}</span> {formatDate(res.date_retour_prevue)}</div>
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                        <div className="text-sm text-gray-900"><span className="font-medium">{t('total')}:</span> {formatCurrency(res.total)}</div>
                                                        <div className="text-sm text-gray-500"><span className="font-medium">{t('paid')}:</span> {formatCurrency(res.total_paye)}</div>
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                        {getStatusBadge(res.status)}
                                                    </td>
                                                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRtl ? 'text-left' : 'text-right'}`}>
                                                        <div className={`flex ${isRtl ? 'justify-start space-x-reverse space-x-2' : 'justify-end space-x-2'}`}>
                                                            <button
                                                                onClick={() => handleDeleteReservation(res.id)}
                                                                className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                                                                title={t('delete')}
                                                            >
                                                                <X className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleViewDetails(res.id)}
                                                                className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                                                                title={t('view_details')}
                                                            >
                                                                <Eye className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditReservation(res.id)}
                                                                className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50"
                                                                title={t('edit')}
                                                            >
                                                                <Edit className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleCompleteReservation(res)}
                                                                className="text-purple-600 hover:text-purple-900 p-1 rounded-full hover:bg-purple-50"
                                                                title={t('complete')}
                                                            >
                                                                ✅
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="block md:hidden space-y-4 p-4">
                                    {reservations.map((res) => (
                                        <div key={res.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
                                            <div className="flex justify-between items-start">
                                                <span className="text-sm font-semibold text-gray-900">#{res.id}</span>
                                                {getStatusBadge(res.status)}
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-sm font-medium text-gray-900"><span className="text-gray-500 font-normal">{t('client')}:</span> {getClientName(res.client_id)} (ID: {res.client_id})</p>
                                                <p className="text-sm text-gray-700"><span className="text-gray-500">{t('vehicle')}:</span> {getVoitureInfo(res.voiture_id)}</p>
                                                <p className="text-xs text-gray-500">{res.lieu_depart} → {res.lieu_retour}</p>
                                            </div>
                                            <div className="border-t border-gray-100 pt-2 space-y-1">
                                                <p className="text-xs text-gray-600"><strong>{t('departure_point')}:</strong> {formatDate(res.date_depart)}</p>
                                                <p className="text-xs text-gray-600"><strong>{t('return_point')}:</strong> {formatDate(res.date_retour_prevue)}</p>
                                            </div>
                                            <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
                                                <div>
                                                    <p className="text-xs text-gray-500">{t('total')}: <span className="font-semibold text-gray-900">{formatCurrency(res.total)}</span></p>
                                                    <p className="text-xs text-gray-500">{t('paid')}: <span className="font-semibold text-green-600">{formatCurrency(res.total_paye)}</span></p>
                                                </div>
                                                <div className="flex space-x-1">
                                                    <button
                                                        onClick={() => handleDeleteReservation(res.id)}
                                                        className="text-red-600 p-2 rounded-lg hover:bg-red-50"
                                                    >
                                                        <X className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleViewDetails(res.id)}
                                                        className="text-blue-600 p-2 rounded-lg hover:bg-blue-50"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditReservation(res.id)}
                                                        className="text-green-600 p-2 rounded-lg hover:bg-green-50"
                                                    >
                                                        <Edit className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleCompleteReservation(res)}
                                                        className="text-purple-600 p-2 rounded-lg hover:bg-purple-50"
                                                    >
                                                        ✅
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {/* Pagination Controls */}
                        {reservations.length > 0 && (
                            <div className={`px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                        onClick={() => paginate(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {t('previous')}
                                    </button>
                                    <button
                                        onClick={() => paginate(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {t('next')}
                                    </button>
                                </div>
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            {t('showing_results', {
                                                start: indexOfFirstItem + 1,
                                                end: indexOfFirstItem + reservations.length,
                                                total: totalItems
                                            })}
                                        </p>
                                    </div>
                                    <div>
                                        <nav className={`relative z-0 inline-flex rounded-md shadow-sm -space-x-px ${isRtl ? 'space-x-reverse' : ''}`} aria-label="Pagination">
                                            <button
                                                onClick={() => paginate(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className={`relative inline-flex items-center px-2 py-2 ${isRtl ? 'rounded-r-md' : 'rounded-l-md'} border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`}
                                            >
                                                <span className="sr-only">{t('previous')}</span>
                                                {isRtl ? t('next') : t('previous')}
                                            </button>
                                            {Array.from({ length: totalPages }, (_, i) => (
                                                <button
                                                    key={i + 1}
                                                    onClick={() => paginate(i + 1)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => paginate(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className={`relative inline-flex items-center px-2 py-2 ${isRtl ? 'rounded-l-md' : 'rounded-r-md'} border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`}
                                            >
                                                <span className="sr-only">{t('next')}</span>
                                                {isRtl ? t('previous') : t('next')}
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div >
        </Sidebar >
    );
};

export default Location;
